import { useAuth } from '../hooks/useAuth'
import { useNavigate } from 'react-router-dom'
import { useEffect, useState } from 'react'
import Navigation from '../components/Navigation'
import { apiClient, type PlayerTopScore, type DashboardData } from '../services/apiClient'

export default function DashboardPage() {
  const { user, logout } = useAuth()
  const navigate = useNavigate()
  const [topScores, setTopScores] = useState<PlayerTopScore[]>([])
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true)
        setError(null)
        
        // Fetch dashboard data which includes top scores by game
        const dashboardResponse = await apiClient.getDashboardData()
        
        setTopScores(dashboardResponse.topScoresByGame)
        setDashboardData(dashboardResponse)
      } catch (err) {
        console.error('Error fetching dashboard data:', err)
        setError(err instanceof Error ? err.message : 'Failed to load dashboard data')
      } finally {
        setLoading(false)
      }
    }

    if (user) {
      fetchDashboardData()
    }
  }, [user])

  const handleLogout = async () => {
    await logout();
  }

  const handleEditProfile = () => {
    navigate('/profile/edit')
  }

  const handleChangePassword = () => {
    navigate('/change-password')
  }

  const handleGameTileClick = (gameId: number) => {
    navigate(`/game/${gameId}/scores`)
  }

  // This shouldn't happen due to route protection, but adding safety check
  if (!user) {
    return (
      <div className="min-h-screen bg-firedup-dark flex items-center justify-center">
        <div className="text-firedup-orange text-xl">Redirecting...</div>
      </div>
    )
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-firedup-dark">
        <Navigation />
        <div className="max-w-7xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="text-firedup-orange text-xl">Loading your dashboard...</div>
          </div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen bg-firedup-dark">
        <Navigation />
        <div className="max-w-7xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="text-red-500 text-xl mb-4">Error loading dashboard</div>
            <div className="text-gray-300">{error}</div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-firedup-dark">
      <Navigation />
      
      <div className="max-w-7xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-black text-white mb-4">
            Welcome back, <span className="text-firedup-orange">{user.displayName}!</span>
          </h1>
          <p className="text-xl text-gray-300">
            Ready to dominate the leaderboards?
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {/* My Games */}
          <div className="bg-firedup-gray/50 backdrop-blur-sm p-6 rounded-lg border border-firedup-light-gray md:col-span-2 lg:col-span-3">
            <h3 className="text-xl font-bold text-white mb-4">🏆 My Games</h3>
            {topScores.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {topScores.map((score, index) => (
                  <div 
                    key={score.gameId} 
                    className="bg-firedup-dark/50 p-4 rounded-lg border border-firedup-light-gray hover:bg-firedup-dark/70 hover:border-firedup-orange transition-all duration-200"
                  >
                    <div className="flex items-center justify-between gap-4 mb-2">
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-1">
                          <h4 className="font-bold text-firedup-orange">{score.gameName}</h4>
                          <span className="text-sm text-gray-400">#{index + 1}</span>
                        </div>
                      </div>
                      <button 
                        onClick={() => handleGameTileClick(score.gameId)}
                        className="px-4 py-2 bg-gray-600 hover:bg-gray-500 text-white text-sm rounded-lg transition-colors duration-200 whitespace-nowrap"
                      >
                        View All Scores →
                      </button>
                    </div>
                    <div className="text-2xl font-bold text-white mb-2">
                      {score.topScore?.toLocaleString() || '0'}
                    </div>
                    <div className="text-sm text-gray-300 mb-2">
                      📍 {score.location || 'Unknown'}
                    </div>
                    <div className="text-sm text-gray-400 mb-2">
                      🗓️ {score.achievedAt ? new Date(score.achievedAt).toLocaleDateString() : 'Unknown'}
                    </div>
                    {score.metadata && (
                      <div className="text-xs text-gray-500">
                        {score.metadata.difficulty && (
                          <span className="mr-2">🎯 {score.metadata.difficulty}</span>
                        )}
                        {score.metadata.character && (
                          <span className="mr-2">👤 {score.metadata.character}</span>
                        )}
                        {score.metadata.level && (
                          <span className="mr-2">🏁 Level {score.metadata.level}</span>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <div className="text-gray-400 text-lg mb-2">🎮 No scores yet!</div>
                <div className="text-gray-500">Play some games to see your top scores here</div>
              </div>
            )}
          </div>

          {/* Quick Actions */}
          <div className="bg-firedup-gray/50 backdrop-blur-sm p-6 rounded-lg border border-firedup-light-gray">
            <h3 className="text-xl font-bold text-white mb-4">Quick Actions</h3>
            <div className="space-y-3">
              <button className="w-full btn-secondary text-left">
                🎮 View Leaderboards
              </button>
              <button className="w-full btn-secondary text-left">
                🏟️ Find Locations
              </button>
            </div>
          </div>

          {/* Stats */}
          <div className="bg-firedup-gray/50 backdrop-blur-sm p-6 rounded-lg border border-firedup-light-gray">
            <h3 className="text-xl font-bold text-white mb-4">Statistics</h3>
            {dashboardData?.statistics ? (
              <div className="space-y-3 text-gray-300">
                <div className="flex justify-between">
                  <span>Total Games:</span>
                  <span className="text-firedup-orange font-bold">{dashboardData.statistics.totalGamesPlayed || 0}</span>
                </div>
                <div className="flex justify-between">
                  <span>This Week:</span>
                  <span className="text-firedup-orange font-bold">{dashboardData.statistics.gamesThisWeek || 0}</span>
                </div>
                <div className="flex justify-between">
                  <span>This Month:</span>
                  <span className="text-firedup-orange font-bold">{dashboardData.statistics.gamesThisMonth || 0}</span>
                </div>
              </div>
            ) : (
              <div className="text-gray-400">Loading statistics...</div>
            )}
          </div>

          {/* Profile */}
          <div className="bg-firedup-gray/50 backdrop-blur-sm p-6 rounded-lg border border-firedup-light-gray">
            <h3 className="text-xl font-bold text-white mb-4">Profile</h3>
            <div className="text-gray-300 space-y-2">
              <p>Name: {user.firstName} {user.lastName}</p>
              <p>Display Name: {user.displayName}</p>
              <div className="flex items-center gap-2">
                <p>Email: {user.email}</p>
                {user.emailVerified && (
                  <span className="inline-flex items-center px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded-full">
                    <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    Verified
                  </span>
                )}
              </div>
              <p>Country: {user.country}</p>
              <p className="mb-4">
                Member since: {user.createdAt ? new Date(user.createdAt).toLocaleDateString() : 'Today'}
              </p>
              <div className="flex gap-3 mb-3">
                <button 
                  onClick={handleEditProfile}
                  className="btn-secondary flex-1"
                >
                  ✏️ Edit Profile
                </button>
                <button 
                  onClick={handleChangePassword}
                  className="btn-secondary flex-1"
                >
                  🔒 Change Password
                </button>
              </div>
              <button 
                onClick={handleLogout}
                className="btn-primary w-full"
              >
                Sign Out
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
